
import { AssessmentResult, FileData } from "../types";
import { extractTextFromFile } from "../utils/fileParser";

const DEFAULT_ASSESSMENT: Partial<AssessmentResult> = {
  overallMark: 0,
  minMark: 0,
  maxMark: 0,
  classification: "Fail",
  criteriaResults: [],
  generalStrengths: [],
  areasForImprovement: [],
  summaryFeedback: "No summary feedback provided."
};

const ensureArray = (val: any): string[] => {
  if (Array.isArray(val)) return val.map(v => String(v));
  if (typeof val === 'string' && val.trim()) return [val];
  return [];
};

export const assessWithAzure = async (
  rubricFile: FileData,
  assignmentFile: FileData,
  apiKey: string,
  endpoint: string,
  deployment: string,
  extraContext: string = ''
): Promise<AssessmentResult> => {
  const rubricText = await extractTextFromFile(rubricFile.base64, rubricFile.mimeType);
  const assignmentText = await extractTextFromFile(assignmentFile.base64, assignmentFile.mimeType);
  const today = new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  
  // Clean endpoint if it has trailing slash
  const cleanEndpoint = endpoint.replace(/\/$/, "");
  // Azure OpenAI API URL structure
  const url = `${cleanEndpoint}/openai/deployments/${deployment}/chat/completions?api-version=2024-02-15-preview`;

  const systemPrompt = `
    You are a Senior Academic Lead in Pharmacy and Pharmaceutical Sciences at the University of Nottingham (UK).
    Evaluate student work against the provided rubric with professional academic judgement.

    Today's date is ${today}. Only flag references as "in the future" if they are dated AFTER ${today}.

    CRITICAL RULES:
    1. RUBRIC INTERPRETATION: The rubric may be a text extraction from a table or a structured JSON object. If JSON, map 'criteria' to 'criteriaResults'. The 'marks' field in JSON represents the maximum marks for that criterion.
    2. VISUAL CONTENT: You are reading text-only. If a criterion refers to figures, structures, or diagrams, set isVisual: true, award a mid-range draft mark, and note that visual evidence was not verified.
    2. UK ENGLISH: Use UK spelling (paracetamol, synthesised, clinical).
    3. PATIENT SAFETY: Flag any clinical errors that would compromise patient safety in 'Areas for Development'.

    MARKING SCALE (UoN):
    - 70%+: 1st | 60-69%: 2:1 | 50-59%: 2:2 | 40-49%: 3rd | <40%: Fail

    ${extraContext ? `SPECIFIC CONTEXT:\n${extraContext}` : ""}

    RESPONSE FORMAT:
    Return ONLY a JSON object: 
    { overallMark, minMark, maxMark, classification, criteriaResults: [{criterion, mark, maxMark, feedback, isVisual}], generalStrengths, areasForImprovement, summaryFeedback }
  `;

  const userPrompt = `
    RUBRIC:
    ${rubricText}

    STUDENT WORK:
    ${assignmentText}
  `;

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'api-key': apiKey
      },
      body: JSON.stringify({
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userPrompt }
        ],
        temperature: 0.1,
        response_format: { type: "json_object" }
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Azure API Error (${response.status}): ${errorText}`);
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    const rawResult = JSON.parse(content);
    const criteriaResults = Array.isArray(rawResult.criteriaResults) ? rawResult.criteriaResults : [];
    
    // Calculate raw totals and percentage
    const totalEarned = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.mark) || 0), 0);
    let totalMax = criteriaResults.reduce((sum: number, c: any) => sum + (Number(c.maxMark) || 0), 0);
    
    // Fallback if maxMark is only provided at the top level
    if (totalMax === 0 && rawResult.maxMark) {
      totalMax = Number(rawResult.maxMark);
    }
    
    const overallMark = totalMax > 0 ? Math.round((totalEarned / totalMax) * 100) : (Number(rawResult.overallMark) || 0);

    const result: AssessmentResult = { 
      ...DEFAULT_ASSESSMENT, 
      ...rawResult,
      criteriaResults,
      totalEarned,
      totalMax,
      overallMark,
      generalStrengths: ensureArray(rawResult.generalStrengths),
      areasForImprovement: ensureArray(rawResult.areasForImprovement)
    } as AssessmentResult;
    
    // Adjusted mark calculation (Excluding Visuals)
    const nonVisual = result.criteriaResults.filter(c => !c.isVisual);
    if (nonVisual.length > 0 && nonVisual.length < result.criteriaResults.length) {
      const earned = nonVisual.reduce((sum, c) => sum + (Number(c.mark) || 0), 0);
      const possible = nonVisual.reduce((sum, c) => sum + (Number(c.maxMark) || 0), 0);
      result.adjustedMark = possible > 0 ? Math.round((earned / possible) * 100) : result.overallMark;
    } else {
      result.adjustedMark = result.overallMark;
    }

    result.fileName = assignmentFile.name;
    return result;
  } catch (err: any) {
    console.error("Azure Assessment Error:", err);
    throw err;
  }
};
